import { FC, useCallback, useMemo } from 'react';

import { IDataSourceItem } from 'Modules/Studio/DataSources/redux/initialState';
import { useIngestionByDataSourceContext } from '../../../context/DataSourceIngestion.context';
import { IActiveIngestion } from 'Modules/Studio/components/StudioInfo/redux/ingestion.interface';
import IngestionButton from './IngestionButton';
import {
  getDSViewIsRunningIngestion,
  getTableViewDataSourceIsDisabled,
  getTableViewDataSourceToolTipText,
} from './helpers';

type TProps = Pick<IDataSourceItem, 'id'> & {
  activeIngestion: IActiveIngestion | null;
};

const TableViewIngestionByDataSourceButton: FC<TProps> = ({ id, activeIngestion }) => {
  const { runIngestionByDS, stopIngestion: stopIngestionById } = useIngestionByDataSourceContext();

  const isRunningIngestion: boolean = useMemo(
    () => getDSViewIsRunningIngestion(activeIngestion),
    [activeIngestion]
  );

  const isDisabled = useMemo(
    () => getTableViewDataSourceIsDisabled(activeIngestion),
    [activeIngestion]
  );

  const toolTipText = useMemo(() => {
    return getTableViewDataSourceToolTipText(isDisabled, isRunningIngestion);
  }, [isDisabled, isRunningIngestion]);

  /**
   * Start ingestion at data source level
   */
  const startIngestion = useCallback(() => {
    runIngestionByDS(id);
  }, [runIngestionByDS, id]);

  /**
   * Stop ingestion at data source level OR stop ingestion at table level(s)
   */
  const stopIngestion = useCallback(() => {
    const activeTables = Object.keys(activeIngestion?.tables || {});
    if (activeTables.length === 0) {
      // ingestion at datasource level
      stopIngestionById(activeIngestion?.ingestionId || '');
    } else {
      // ingestion is running from multiple tables
      Object.keys(activeIngestion?.tables || {}).forEach((tableName) => {
        stopIngestionById(activeIngestion?.tables?.[tableName]?.ingestionId || '');
      });
    }
  }, [activeIngestion, stopIngestionById]);

  return (
    <IngestionButton
      id={id}
      isRunningIngestion={isRunningIngestion}
      isDisabled={isDisabled}
      toolTipText={toolTipText}
      startIngestion={startIngestion}
      stopIngestion={stopIngestion}
    />
  );
};

export default TableViewIngestionByDataSourceButton;
